import { useState, Suspense } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { Eye } from 'lucide-react'
import PageLoadingSpinner from './PageLoadingSpinner'
import { useAuth } from '../hooks/useAuth'
import { useProfile } from '../hooks/useProfile'
import { PortfolioProvider } from '../context/PortfolioContext'
import { TeamContextProvider, useTeamContext } from '../context/TeamContext'
import { DueRecurringExpensesProvider } from '../context/DueRecurringExpensesContext'
import SubscriptionBanner from './SubscriptionBanner'
import WriteActionGuard from './WriteActionGuard'
import TermsAcceptanceModal, { useTermsAccepted } from './TermsAcceptanceModal'
import { useBadgeCounts } from '../hooks/useBadgeCounts'
import { useGlobalSearch } from '../hooks/useGlobalSearch'
import { BASE_NAV_GROUPS, AGENCY_NAV_GROUP, NAV_ITEMS } from '../types/navigation'
import Sidebar from './layout/Sidebar'
import DesktopHeader from './layout/DesktopHeader'
import MobileHeader from './layout/MobileHeader'
import MobileBottomNav from './layout/MobileBottomNav'
import MoreMenu from './layout/MoreMenu'

export default function LandlordLayout() {
  return (
    <TeamContextProvider>
      <LandlordLayoutInner />
    </TeamContextProvider>
  )
}

function LandlordLayoutInner() {
  const { user, signOut } = useAuth()
  const { profile } = useProfile()
  const location = useLocation()
  const [showMoreMenu, setShowMoreMenu] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const termsStatus = useTermsAccepted(user?.id)
  const { isTeamMember, teamRole, financialAccess, agencyName } = useTeamContext()

  // Show modal only when DB check confirms terms not accepted (termsStatus === false)
  // Don't show while loading (termsStatus === null) to avoid flash
  const showTermsModal = termsStatus === false && !termsAccepted

  const handleTermsAccept = () => {
    setTermsAccepted(true)
  }

  const { getBadgeCount } = useBadgeCounts()
  const {
    searchQuery,
    setSearchQuery,
    searchResults,
    setSearchResults,
    showSearchResults,
    setShowSearchResults,
    searchLoading,
    searchRef,
    handleSearchChange,
    handleResultClick,
  } = useGlobalSearch()

  const isAgent = profile?.role === 'agent'

  // Build nav groups — filter team-restricted items when isTeamMember
  let navGroups = isAgent
    ? [...BASE_NAV_GROUPS.slice(0, 1), AGENCY_NAV_GROUP, ...BASE_NAV_GROUPS.slice(1)]
    : BASE_NAV_GROUPS

  let moreItems = isAgent
    ? [...NAV_ITEMS.more, { to: '/portfolios', icon: '💼', label: 'Portfolios' }, { to: '/team', icon: '👥', label: 'Team' }]
    : NAV_ITEMS.more

  if (isTeamMember) {
    const hiddenRoutes = new Set<string>()

    // Viewer/Manager cannot see Team (admin and owners can)
    if (teamRole !== 'admin') hiddenRoutes.add('/team')

    // Hide Financials when no financial access
    if (!financialAccess) hiddenRoutes.add('/financials')

    if (hiddenRoutes.size > 0) {
      navGroups = navGroups.map(group => ({
        ...group,
        items: group.items.filter(item => !hiddenRoutes.has(item.to)),
      }))
      moreItems = moreItems.filter(item => !hiddenRoutes.has(item.to))
    }
  }

  const isMoreActive = moreItems.some(item => location.pathname.startsWith(item.to))
  const firstName = profile?.full_name?.split(' ')[0] || 'there'

  return (
    <PortfolioProvider>
    <DueRecurringExpensesProvider>
    {showTermsModal && <TermsAcceptanceModal userType={isAgent ? 'agent' : 'landlord'} onAccept={handleTermsAccept} />}
    <div className="dashboard-layout">
      <Sidebar
        navGroups={navGroups}
        getBadgeCount={getBadgeCount}
        onSignOut={signOut}
        isTeamMember={isTeamMember}
      />

      {/* Main Content Area */}
      <div className="flex-1 lg:ml-64 overflow-y-auto overflow-x-hidden pb-20 lg:pb-0" style={{ background: 'var(--bg-surface)' }}>
        <MobileHeader
          searchQuery={searchQuery}
          searchResults={searchResults}
          showSearchResults={showSearchResults}
          searchLoading={searchLoading}
          onSearchChange={handleSearchChange}
          onResultClick={handleResultClick}
          onSearchFocus={() => searchQuery && setShowSearchResults(true)}
          setSearchQuery={setSearchQuery}
          setSearchResults={setSearchResults}
          setShowSearchResults={setShowSearchResults}
        />

        <DesktopHeader
          firstName={firstName}
          isAgent={isAgent}
          isTeamMember={isTeamMember}
          agencyName={agencyName}
          searchQuery={searchQuery}
          searchResults={searchResults}
          showSearchResults={showSearchResults}
          searchLoading={searchLoading}
          searchRef={searchRef}
          onSearchChange={handleSearchChange}
          onResultClick={handleResultClick}
          onSearchFocus={() => searchQuery && setShowSearchResults(true)}
        />

        {/* Subscription status banner */}
        <SubscriptionBanner />

        {/* View-only badge for viewer team members */}
        {teamRole === 'viewer' && (
          <div
            className="flex items-center gap-2 px-4 lg:px-8 py-2"
            style={{ background: 'var(--color-accent-muted)', borderBottom: '1px solid var(--border-color-light)' }}
          >
            <Eye className="w-3.5 h-3.5" style={{ color: 'var(--color-accent)' }} />
            <span className="text-xs font-medium" style={{ color: 'var(--color-accent)' }}>
              View-only access
            </span>
          </div>
        )}

        <main className="p-4 lg:p-8 2xl:!max-w-[1600px]" style={{ maxWidth: 1400, width: '100%', boxSizing: 'border-box', margin: '0 auto' }}>
          <WriteActionGuard>
            <Suspense fallback={<PageLoadingSpinner />}>
              <Outlet />
            </Suspense>
          </WriteActionGuard>
        </main>
      </div>

      <MobileBottomNav
        moreItems={moreItems}
        isMoreActive={isMoreActive}
        getBadgeCount={getBadgeCount}
        onMoreClick={() => setShowMoreMenu(true)}
      />

      {showMoreMenu && (
        <MoreMenu
          moreItems={moreItems}
          getBadgeCount={getBadgeCount}
          onClose={() => setShowMoreMenu(false)}
          onSignOut={signOut}
        />
      )}
    </div>
    </DueRecurringExpensesProvider>
    </PortfolioProvider>
  )
}
